import {
  QualityRegimeOptions,
  constructionStandardOptions,
  correctionReasonOptions,
  deliveryContextGMWOptions,
  electrodePackingMaterialOptions,
  electrodeStatusOptions,
  glueOptions,
  groundLevelPositioningMethodOptions,
  indicationYesNoOptions,
  initialFunctionOptions,
  optionalIndicationYesNoOptions,
  positioningMethodOptions,
  qualityRegimeOptions,
  registrationTypeOptions,
  screenProtectionOptions,
  sockMaterialOptions,
  tubeMaterialOptions,
  tubePackingMaterialOptions,
  tubeStatusOptions,
  tubeTopPositioningMethodOptions,
  tubeTypeOptions,
  verticalDatumOptions,
  wellHeadProtectorOptions,
  wellStabilityOptions,
} from "@nens/brostar/uploadapi-client";

import { BrostarUiSchema, getEnumNamesAndDisabled } from "@/libs/brostar-form";
/**
 * UI Schema library for GMW forms with reusable field configurations
 * Returns a flat object with all field UI schema configurations
 */
export const createGmwUiSchemaLibrary = (qualityRegime: QualityRegimeOptions | undefined) =>
  ({
    // Metadata fields
    projectNumber: {},
    qualityRegime: getEnumNamesAndDisabled(qualityRegimeOptions, qualityRegime),
    deliveryAccountableParty: {
      "ui:widget": "OrganisationSelect" as const,
    },
    broId: {},
    correctionReason: getEnumNamesAndDisabled(correctionReasonOptions, qualityRegime),

    // Registration type
    registrationType: getEnumNamesAndDisabled(registrationTypeOptions, qualityRegime),

    // Event-specific fields
    eventDate: {},
    wellStability: getEnumNamesAndDisabled(wellStabilityOptions, qualityRegime),
    groundLevelStable: getEnumNamesAndDisabled(indicationYesNoOptions, qualityRegime),
    groundLevelPosition: {},
    groundLevelPositioningMethod: getEnumNamesAndDisabled(
      groundLevelPositioningMethodOptions,
      qualityRegime
    ),

    // Optional
    isAbroad: getEnumNamesAndDisabled(optionalIndicationYesNoOptions, qualityRegime),
    geometricDataPubliclyAvailable: getEnumNamesAndDisabled(
      optionalIndicationYesNoOptions,
      qualityRegime
    ),
    relatedSurveys: {},

    // Insertion fields
    insertedPartLength: {},
    insertedPartDiameter: {},
    insertedPartMaterial: getEnumNamesAndDisabled(tubeMaterialOptions, qualityRegime),

    // Party fields
    wellHeadProtector: getEnumNamesAndDisabled(wellHeadProtectorOptions, qualityRegime),
    maintenanceResponsibleParty: {
      "ui:widget": "OrganisationSelect" as const,
    },
    owner: {
      "ui:widget": "OrganisationSelect" as const,
    },

    // GMW Start Registration fields
    objectIdAccountableParty: {},
    nitgCode: {},
    deliveryContext: getEnumNamesAndDisabled(deliveryContextGMWOptions, qualityRegime),
    constructionStandard: getEnumNamesAndDisabled(constructionStandardOptions, qualityRegime),
    initialFunction: getEnumNamesAndDisabled(initialFunctionOptions, qualityRegime),
    numberOfMonitoringTubes: {
      "ui:widget": "Hidden",
    },
    wellConstructionDate: {},
    deliveredLocation: {},
    horizontalPositioningMethod: getEnumNamesAndDisabled(positioningMethodOptions, qualityRegime),
    localVerticalReferencePoint: {},
    offset: {},
    verticalDatum: getEnumNamesAndDisabled(verticalDatumOptions, qualityRegime),

    // Monitoring tube fields (for single tube in event forms)
    tubeNumber: {},
    tubeTopPosition: {},
    tubeTopPositioningMethod: getEnumNamesAndDisabled(
      tubeTopPositioningMethodOptions,
      qualityRegime
    ),
    variableDiameter: getEnumNamesAndDisabled(indicationYesNoOptions, qualityRegime),
    tubeTopDiameter: {},
    tubeMaterial: getEnumNamesAndDisabled(tubeMaterialOptions, qualityRegime),
    glue: getEnumNamesAndDisabled(glueOptions, qualityRegime),
    plainTubePartLength: {},
    tubeStatus: getEnumNamesAndDisabled(tubeStatusOptions, qualityRegime),

    // Full monitoring tube fields (for GMW form)
    tubeType: getEnumNamesAndDisabled(tubeTypeOptions, qualityRegime),
    artesianWellCapPresent: getEnumNamesAndDisabled(indicationYesNoOptions, qualityRegime),
    sedimentSumpPresent: getEnumNamesAndDisabled(indicationYesNoOptions, qualityRegime),
    numberOfGeoOhmCables: { "ui:widget": "Hidden" },
    tubePackingMaterial: getEnumNamesAndDisabled(tubePackingMaterialOptions, qualityRegime),
    screenLength: {},
    sockMaterial: getEnumNamesAndDisabled(sockMaterialOptions, qualityRegime),
    sedimentSumpLength: {},
    screenProtection: getEnumNamesAndDisabled(screenProtectionOptions, qualityRegime),

    // Geo-ohm cable fields
    cableNumber: {},

    // Electrode fields
    electrodeNumber: {},
    electrodePackingMaterial: getEnumNamesAndDisabled(
      electrodePackingMaterialOptions,
      qualityRegime
    ),
    electrodeStatus: getEnumNamesAndDisabled(electrodeStatusOptions, qualityRegime),
    electrodePosition: {},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } satisfies Record<string, BrostarUiSchema<{ [key: string]: any }>[string]>);

export type GmwUiSchemaLibrary = ReturnType<typeof createGmwUiSchemaLibrary>;
